import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import Callout from "../../../elements/V2/Callout"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Grid from "../../../elements/V2/Grid"
import Section from "../../../elements/V2/Section"
import Typography from "../../../elements/V2/Typography"
import Video from "../../../experiments/Video"
import HeroLayout from "../HeroLayout"

import { useElementObserver } from "@hooks/V2/useElementObserver"
import { usePageLayoutState } from "@utils/layout"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import {
  getBackgroundColorClass,
  getAccentColor,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"
import React, { useEffect, useRef } from "react"

type Props = {
  headingText: string
  headingSize?: "display1" | "h1" | "h2" | "h3" | "h4" | "h5"
  subHeadingText?: string
  headingMobileAlignment?: "left" | "center"
  bodyText: string
  primaryBackgroundColor: Color
  secondaryBackgroundColor?: Color
  primaryButtonText?: string
  primaryButtonLink?: string | YocoCom.YocoLink
  primaryButtonMobileText?: string
  primaryButtonMobileLink?: string | YocoCom.YocoLink
  primaryButtonBorderColor: Color
  secondaryButtonText: string
  secondaryButtonLink: string | YocoCom.YocoLink | undefined
  videoUrl?: string
  imageUrl: string
  imageAlt: string
  mobileImageUrl?: string
  callout?: Storyblok.Callout
  hasScrollArrow?: boolean
  hideMobileStickyButton?: boolean
  secondaryTrackingEvent: string
  secondaryTrackingEventKey?: string
  secondaryTrackingEventValue?: string
} & Partial<Storyblok.CTATrackingEvent>

const CentreHero = ({
  headingText,
  subHeadingText,
  headingSize = "h2",
  headingMobileAlignment = "center",
  bodyText,
  primaryBackgroundColor,
  secondaryBackgroundColor,
  primaryButtonText,
  primaryButtonLink,
  primaryButtonMobileLink,
  primaryButtonMobileText,
  primaryButtonBorderColor,
  secondaryButtonText,
  secondaryButtonLink,
  videoUrl,
  imageUrl,
  imageAlt,
  mobileImageUrl,
  callout,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  hideMobileStickyButton,
  secondaryTrackingEvent,
  secondaryTrackingEventKey,
  secondaryTrackingEventValue,
  ...props
}: Props) => {
  const { setHeroCTAVisible, hasAnchorNavigation } = usePageLayoutState()
  const textContainerRef = useRef(null)
  const { isVisible: isCtaVisible, startObserving } =
    useElementObserver(textContainerRef)
  const accentColor = getAccentColor(primaryBackgroundColor)

  useEffect(() => {
    setHeroCTAVisible(isCtaVisible)

    return () => {
      setHeroCTAVisible(false)
    }
  }, [isCtaVisible, setHeroCTAVisible])

  startObserving()

  return (
    <HeroLayout
      primaryButtonText={primaryButtonMobileText ?? primaryButtonText}
      primaryButtonLink={primaryButtonLink}
      primaryButtonMobileLink={primaryButtonMobileLink}
      primaryButtonColor={primaryButtonBorderColor}
      hideMobileButton={hideMobileStickyButton}
      trackingEvent={trackingEvent}
      trackingEventKey={trackingEventKey}
      trackingEventValue={trackingEventValue}
      className="overflow-auto md:overflow-hidden"
    >
      <div
        className={classNames(
          "h-full pb-64 pt-72",
          hasAnchorNavigation ? "md:pt-88" : "md:py-88 lg:py-88",
          !secondaryBackgroundColor
            ? getBackgroundColorClass(primaryBackgroundColor)
            : [
                "bg-gradient-to-br",
                getGradientFromColorClass(primaryBackgroundColor),
                getGradientToColorClass(secondaryBackgroundColor),
              ]
        )}
        {...props}
      >
        <Section
          className={classNames(
            !secondaryBackgroundColor
              ? getBackgroundColorClass(primaryBackgroundColor)
              : [
                  "bg-gradient-to-br",
                  getGradientFromColorClass(primaryBackgroundColor),
                  getGradientToColorClass(secondaryBackgroundColor),
                ]
          )}
          {...props}
        >
          <Grid>
            <div
              ref={textContainerRef}
              className="col-span-full md:col-start-2 md:col-end-6 lg:col-start-3 lg:col-end-11"
            >
              {callout && (
                <Callout
                  color={getAccentColor(primaryBackgroundColor)}
                  classname={classNames("mb-16", {
                    "mx-auto": headingMobileAlignment === "center",
                  })}
                  text={callout.text}
                  highlightedWord={callout.highlightedWord}
                  highlightColor={callout.highlightColor}
                  hasBorder={callout.hasBorder}
                  hasIcon={callout.hasIcon}
                  ctaText={callout.ctaText}
                  ctaLink={storyblokLinkToYocoLink(callout.ctaLink)}
                />
              )}
              <div
                className={classNames(
                  "mb-8 md:mb-16 md:text-center",
                  headingMobileAlignment === "center"
                    ? "text-center"
                    : "text-left"
                )}
              >
                <Typography
                  text={headingText}
                  font="grotesk"
                  weight="medium"
                  size={headingSize}
                  color={accentColor}
                  element="h1"
                />
                {subHeadingText && (
                  <Typography
                    text={subHeadingText}
                    font="grotesk"
                    weight="medium"
                    size="text-hero-lead-lg"
                    color={accentColor}
                    className="pt-8"
                  />
                )}
              </div>
              <Typography
                text={bodyText}
                font="grotesk"
                weight="book"
                size="body-lg"
                color={accentColor}
                className={classNames(
                  "mb-20 md:mb-32 md:text-center",
                  headingMobileAlignment === "center"
                    ? "text-center"
                    : "text-left"
                )}
              />
              <div className="flex flex-col items-center justify-center gap-20 py-10 sm:flex-row sm:items-baseline sm:justify-center md:gap-24 lg:gap-32">
                {primaryButtonText && primaryButtonLink && (
                  <PillButton
                    size="large"
                    style="solid"
                    text={primaryButtonText}
                    color={primaryButtonBorderColor}
                    linkUrl={primaryButtonLink}
                    mobileLinkUrl={primaryButtonMobileLink}
                    trackingEvent={trackingEvent}
                    trackingEventKey={trackingEventKey}
                    trackingEventValue={trackingEventValue}
                  />
                )}
                <TextButton
                  style="text"
                  text={secondaryButtonText}
                  linkUrl={secondaryButtonLink}
                  color={accentColor}
                  trackingEvent={secondaryTrackingEvent}
                  trackingEventKey={secondaryTrackingEventKey}
                  trackingEventValue={secondaryTrackingEventValue}
                />
              </div>
            </div>
            <div className="col-span-full hidden text-center sm:block">
              {videoUrl ? (
                <Video
                  video={videoUrl}
                  coverImage={imageUrl}
                  ImageAlt={imageAlt || "Hero image"}
                  loadType="eager"
                  aspectRatio="16:9"
                  playbackMode="autoplay"
                  rounded
                  playOnMobile={false}
                />
              ) : (
                <GatsbyStoryblokImage
                  image={imageUrl}
                  alt={imageAlt || "Hero image"}
                  loadType="eager"
                  quality={80}
                  aspectRatio="16:9"
                  rounded
                />
              )}
            </div>
            <div className="col-span-full block text-center sm:hidden">
              {videoUrl ? (
                <Video
                  video={videoUrl}
                  coverImage={imageUrl}
                  ImageAlt={imageAlt || "Hero image"}
                  loadType="eager"
                  aspectRatio="16:9"
                  playbackMode="autoplay"
                  rounded
                  playOnMobile={false}
                />
              ) : (
                <GatsbyStoryblokImage
                  image={mobileImageUrl || imageUrl}
                  alt={imageAlt || "Hero image"}
                  loadType="eager"
                  quality={80}
                  aspectRatio="1:1"
                  rounded
                />
              )}
            </div>
          </Grid>
        </Section>
      </div>
    </HeroLayout>
  )
}

export default CentreHero
